import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { getEntityDisplayName } from '../../../../extViewmodels/Utils';
import { IModalContext } from '../../../../models';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { toast } from 'sonner';
import { useEmailCampaignApprovalRequestMutation } from '../../../../queries';
import { DeprecatedTextArea } from '../../../../web/components/DeprecatedTextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../web/components/Modal';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IModalContext<boolean> {
	campaigns: Api.ICampaign[];
	className?: string;
	styles?: StyleDeclarationValue[];
}

function _BulkEmailApprovalReminderComposer({ styles, className, campaigns, parentModal }: IProps) {
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('BulkEmailApprovalReminderComposer');
	const [note, setNote] = React.useState(() => {
		return campaigns?.length === 1 && campaigns[0]?.approvalRequests?.length > 0
			? `Hi ${campaigns[0].approvalRequests[0].approver?.firstName},`
			: 'Hi,';
	});
	const [isBusy, setIsBusy] = React.useState(false);
	const sendEmailCampaignApprovalRequestMutation = useEmailCampaignApprovalRequestMutation();
	const sendReminder = () => {
		setIsBusy(true);
		const promises = campaigns.map(campaign => {
			const trimmed = (note || '').trim();
			if (!!trimmed && !!campaign) {
				const content = Api.createRawRichTextContentStateWithText(trimmed);
				if (campaign.status === Api.EmailSendStatus.WaitingForApproval && content?.document) {
					logEvent('SendReminder');
					return sendEmailCampaignApprovalRequestMutation.mutateAsync({
						campaignId: campaign.id,
						emailContent: content,
						impersonationContext: {
							account: {
								id: campaign.approvalRequests[0].approver?.accountId,
							},
						},
					});
				}
			}
		});
		Promise.all(promises)
			.then(() => {
				toast.success(`Reminder email${campaigns.length > 1 ? 's' : ''} sent!`);
				parentModal?.onRequestClose(true, false);
			})
			.catch((error: Api.IOperationResultNoValue) => {
				logApiError('SendReminder-Error', error);
				errorMessages.pushApiError(error);
			})
			.finally(() => {
				setIsBusy(false);
			});
	};
	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} bulk-email-approval-reminder-composer ${
				className || ''
			}`}
		>
			<div className={css(styleSheet.title)}>{`Resend Email Reminder${campaigns?.length > 1 ? 's' : ''}`}</div>
			<div className={css(styleSheet.message)}>
				{`Leave a reminder for the client${
					campaigns?.length > 1 ? 's' : ''
				} below. Levitate will send this along with the templates you've picked out `}
				<b>from your email.</b>
			</div>
			{campaigns?.length > 1 && (
				<div className={css(styleSheet.message)}>
					{campaigns.map((campaign, i) => {
						return (
							<div key={`contact${i}`}>
								{campaign.approvalRequests.length > 0
									? getEntityDisplayName(campaign.approvalRequests[0].approver)
									: ''}
							</div>
						);
					})}
				</div>
			)}
			<div>
				<DeprecatedTextArea
					autoFocus={true}
					disabled={isBusy}
					inputClassName={css(styleSheet.inputField)}
					inputId='bulk-email-approval-reminder-composer-input'
					onChange={ev => setNote(ev.target.value || '')}
					value={note || ''}
				/>
			</div>
			<button className={css([baseStyleSheet.ctaButton, styleSheet.button])} disabled={isBusy} onClick={sendReminder}>
				<span>Send</span>
			</button>
			{isBusy && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
		</div>
	);
}

const BulkEmailApprovalReminderComposerAsObserver = observer(_BulkEmailApprovalReminderComposer);
export const BulkEmailApprovalReminderComposerModal = asModalComponent(BulkEmailApprovalReminderComposerAsObserver, {
	className: `bulk-email-approval-reminder-composer-modal`,
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
